/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { matchPath, RouteComponentProps, RouteProps } from "react-router-dom";
/* eslint-disable max-lines */
import { Store } from "redux";
import { SagaMiddleware } from "redux-saga";

import {
  AnyAction,
  AnyPartnerConfiguration,
  getMarketInfo,
  PartnerRouteConfig,
  ResourceStrings,
  RouteConfig,
} from "@gamedev-web/gamedev-sdk-core";
import { toError } from "@gaming/data-validation";
import {
  LCE_NOT_REQUESTED,
  lceContent,
  lceError,
  lceFetching,
  lceNotRequested,
  LCEType,
} from "@gaming/lce";

import { PartnerRenderer } from "../components/PartnerRenderer";
import { StoreWithInjectReducer } from "../redux/createInjectableStore";
import { RootState } from "../redux/reducers/rootReducer";
import { initializeRoutes } from "../routes";
import {
  IAsyncPartnerConfigurationManager,
  ParterLoadingStateMap,
  PartnerConfigLce,
} from "./types";

type AppStore = StoreWithInjectReducer<Store<RootState, AnyAction>>;

export class PartnerConfigManager implements IAsyncPartnerConfigurationManager {
  private _partnerConfigurations: { [partnerId: string]: AnyPartnerConfiguration } = {};
  private _routeConfigs: RouteConfig[] = [];
  private _partnersResourceStringsDict: Dictionary<ResourceStrings> = {};
  private _partnerComponents: WeakMap<object, React.FC<RouteComponentProps<any>>> = new WeakMap();

  /* Async Loading */
  private _subscriptions: Set<(partnerId: string) => void> = new Set();
  private _partnerState: ParterLoadingStateMap = {};
  private _partnerLoadingPromises: { [partnerId: string]: Promise<void> | undefined } = {};
  private _store?: AppStore;
  private _middleware?: SagaMiddleware;

  /**
   * Set the partner configurations to be loaded on demand. The order of the partner configs
   * is the order their routes will be combined in and thus the order react-router will
   * receive rendered <Route />. This matters in react-router v5 but not in v6.
   */
  public setPartnerPackages(configurations: AnyPartnerConfiguration[]): AnyPartnerConfiguration[] {
    this._validatePartnerConfigs(configurations);
    this._processConfigs(configurations);
    initializeRoutes(this._routeConfigs);

    return Object.values(this._partnerConfigurations);
  }

  /**
   * Set the Injectable Redux Store. This store will be injected with partner reducers
   * as partner routes are navigated to.
   */
  public setStore(store: AppStore) {
    this._store = store;
  }

  /**
   * Set saga middleware to run partner sagas as their routes are navigated to.
   * Only applicable to Client.
   */
  public setSagaMiddleware(sagaMiddleware: SagaMiddleware) {
    this._middleware = sagaMiddleware;
  }

  /**
   * Get all partner route configs preserving the order of the partner configs passed in.
   * A simple way to think of this would be if given
   * `[PartnerConfigA, PartnerConfigB, PartnerConfigC]` then we return
   * `[...PartnerConfigA.routes, ...PartnerConfigB.routes, ...PartnerConfigC.routes]`
   */
  public getPartnerRouteConfigs = (): RouteConfig[] => [...this._routeConfigs];

  /**
   * Get the currently loaded partner resources. As partner routes are navigated to, this will
   * update with additional partner strings.
   */
  public getPartnerResources = () => this._partnersResourceStringsDict;

  /**
   * Get a map of all the partner configs.
   */
  public getPartnerConfigMap(): Readonly<{ [partnerId: string]: AnyPartnerConfiguration }> {
    return this._partnerConfigurations;
  }

  /**
   * Call once per page initializeClient() / getRequestHandler(). This ensures
   * that when a new redux store is created, the partner configurations are ready
   * to be loaded on demand.
   */
  public async initialize(): Promise<void> {
    // Any time we initialize, we need to load the partner configs. Reset this state.
    this._getPartnerConfigs().forEach(partner => {
      this._partnerState[partner.partnerId] = lceNotRequested();
    });

    this._partnerLoadingPromises = {};

    await this._loadAllConfigs();
  }

  /**
   * Returns the partner configuration containing the route that matches the path.
   */
  public getPartnerConfigForPath = (path: string): AnyPartnerConfiguration | undefined =>
    this._getPartnerConfigs().find(config => config.routes?.some(route => matchPath(path, route)));

  /**
   * Returns the client state. React will only ask for this on the client. The first call to this must be the same as the server.
   */
  public getClientState = () => this._partnerState;

  /**
   * Returns the server state. React will only ask for this on the server. When the client loads, it must return the same value.
   */
  public getServerState = () => this._partnerState;

  /**
   * Subscribe to Partner Config loading state updates.
   */
  public subscribe = (callback: (partnerId: string) => void) => {
    this._subscriptions.add(callback);

    return () => this._subscriptions.delete(callback);
  };

  /**
   * Load the configuration that holds the matched route of the path.
   * Skips loading the configuration if it is already loaded.
   */
  public loadConfigurationForPath = async (path: string) => {
    const config = this.getPartnerConfigForPath(path);

    if (!config) {
      console.error(`Failed to find matching config for path: ${path}`);
      return;
    }

    try {
      await this._loadPartnerConfigIfReady(config);
    } catch (e) {
      const error = toError(e);
      console.error(`Failed to load partner configuration ${config.partnerId}`, error);

      this._setPartnerState(config.partnerId, lceError(error));
    }
  };

  public loadPartner = async (partnerId: string) => {
    const config = this._partnerConfigurations[partnerId];

    if (!config) {
      const error = new Error(
        `Partner config with id '${partnerId}' not registered before attempting to load`
      );

      console.error(error.message, error);

      this._setPartnerState(partnerId, lceError(error));

      // Re-throw to alert dev consumers to this error.
      throw error;
    }

    try {
      await this._loadPartnerConfigIfReady(config);
    } catch (e) {
      const error = toError(e);
      console.error(`Failed to load partner configuration ${partnerId}`, error);

      this._setPartnerState(config.partnerId, lceError(error));
    }
  };

  private _getPartnerConfigs(): AnyPartnerConfiguration[] {
    return Object.values(this._partnerConfigurations);
  }

  private _validatePartnerConfigs(inputConfigs: AnyPartnerConfiguration[]) {
    if (inputConfigs.length === 0) {
      throw new Error("No partners configured");
    }

    const hasRoutes = inputConfigs.some(config => (config.routes?.length || 0) > 0);

    if (!hasRoutes) {
      throw new Error("No routes configured");
    }

    // ensure all partner names are unique
    const partnerNames = inputConfigs.map(config => config.partnerId);
    const uniquePartnerNames = new Set(partnerNames);
    if (partnerNames.length !== uniquePartnerNames.size) {
      throw new Error(
        `Duplicate partner name found. Partners Configured: ${JSON.stringify(partnerNames)}`
      );
    }
  }

  private _processConfigs(configs: AnyPartnerConfiguration[]) {
    const processedConfigs: { [parterId: string]: AnyPartnerConfiguration } = {};
    const routeConfigs: RouteConfig[] = [];

    this._partnerState = {};

    configs.forEach(config => {
      const processedPartner: AnyPartnerConfiguration = this._mapPartnerConfig(config);

      if (processedPartner.routes) {
        routeConfigs.push(...processedPartner.routes);
      }

      processedConfigs[config.partnerId] = processedPartner;

      this._partnerState[config.partnerId] = LCE_NOT_REQUESTED;
    });

    this._routeConfigs = routeConfigs;
    this._partnerConfigurations = processedConfigs;
  }

  /**
   * Async Loading
   */
  private async _loadPartnerConfigIfReady(partnerConfig: AnyPartnerConfiguration) {
    const { partnerId } = partnerConfig;

    switch (this._getPartnerLce(partnerId)) {
      case LCEType.Content:
        // No-op
        break;
      case LCEType.Fetching: {
        const existingPromise = this._partnerLoadingPromises[partnerId];

        if (existingPromise) {
          console.log(
            `[PartnerConfigManager] ${Date.now()} Already Loading Partner Config '${partnerId}' - Waiting on loading promise`
          );
          await existingPromise;
          console.log(
            `[PartnerConfigManager] ${Date.now()} Done waiting on promise '${partnerId}'`
          );
        } else {
          console.log(
            `[PartnerConfigManager] ${Date.now()} Already Loading Partner Config '${partnerId}' - No loading promise`
          );
        }

        break;
      }
      case LCEType.Error: {
        console.log(`[PartnerConfigManager] ${Date.now()} Partner Config '${partnerId}'`);
        break;
      }
      case LCEType.NotRequested: {
        console.log(`[PartnerConfigManager] ${Date.now()} Loading Partner Config '${partnerId}'`);
        const loadingPromise = this._loadPartnerConfig(partnerConfig);
        this._partnerLoadingPromises[partnerId] = loadingPromise;
        await loadingPromise;
        this._partnerLoadingPromises[partnerId] = undefined;
        break;
      }
    }
  }

  private async _loadPartnerConfig(partnerConfig: AnyPartnerConfiguration) {
    const { partnerId } = partnerConfig;
    this._setPartnerState(partnerId, lceFetching());

    try {
      if (partnerConfig.lazyConfig && this._store) {
        const { redux, getResourceStrings } = await partnerConfig.lazyConfig();
        /**
         * Load Redux
         */

        if (redux) {
          const { reducer, sagas } = redux;

          if (reducer && this._store) {
            this._store.injectReducer(partnerId, reducer);
          }

          if (sagas && this._middleware) {
            const middleware = this._middleware;
            sagas.forEach(saga => middleware.run(saga));
          }
        }

        /**
         * Load Strings
         */

        if (getResourceStrings) {
          const state = this._store.getState();
          const marketInfo = getMarketInfo(state);

          this._partnersResourceStringsDict[partnerId] = await getResourceStrings(marketInfo);
          this._partnersResourceStringsDict = { ...this._partnersResourceStringsDict };
        }
      }

      console.log(`[PartnerConfigManager] Loaded Partner Config '${partnerId}'`);

      this._setPartnerState(partnerId, lceContent(partnerConfig));
    } catch (e) {
      const error = toError(e);
      console.error(`Failed to load partner ${partnerId}`, error);

      this._setPartnerState(partnerId, lceError(error));
    }
  }

  /**
   * @deprecated Removed once `initializeAllPartnersAsync` is removed and redux dependent
   * components used in Edgewater are conditionally rendered only after Core Partner is loaded.
   * We must load all configs due to the interweaved dependent dependencies between
   * the partners. This leads to Partner A requiring the redux found in Partner B.
   * Rather than be very specific and carefully only load dependent partners per route,
   * it is safer to load all of them.
   */
  private async _loadAllConfigs() {
    for (const config of this._getPartnerConfigs()) {
      await this._loadPartnerConfigIfReady(config);
    }
  }

  private _setPartnerState(partnerId: string, state: PartnerConfigLce) {
    this._partnerState[partnerId] = state;

    // Mutate reference for equality check for subscribers.
    this._partnerState = {
      ...this._partnerState,
    };

    this._subscriptions.forEach(callback => callback(partnerId));
  }

  private _processPartnerRoute(
    partnerRoute: PartnerRouteConfig,
    config: AnyPartnerConfiguration
  ): RouteConfig {
    const { partnerId } = config;
    const { component: Component, redirectComponent, ...route } = partnerRoute;
    const routeConfig: RouteConfig = route;

    // Convert redirect components to regular components that do not require to be lazy loaded.
    let RouteComponent: RouteProps["component"];

    if (redirectComponent) {
      RouteComponent = redirectComponent;
    } else if (Component) {
      // Wrap component, lazy loaded or not, in AsyncPartnerRoute.
      // All partner route components may not access partner resources until the partner config is loaded.
      // We only wrap partner components once per component reference. This allows
      // partners to reuse components between routes for their own optimization.
      const ExistingAsyncComponent = this._partnerComponents.get(Component);
      if (ExistingAsyncComponent) {
        RouteComponent = ExistingAsyncComponent;
      } else {
        const AsyncComponent = (routeProps: RouteComponentProps<any>) => (
          <PartnerRenderer partnerId={partnerId}>
            <Component {...routeProps} />
          </PartnerRenderer>
        );
        this._partnerComponents.set(Component, AsyncComponent);
        RouteComponent = AsyncComponent;
      }
    }

    return {
      ...routeConfig,
      key: `${partnerId}_${routeConfig.key}`,
      component: RouteComponent,
    };
  }

  private _mapPartnerConfig(config: AnyPartnerConfiguration): AnyPartnerConfiguration {
    const { routes, ...partner } = config;

    // Config is not asynchronously loaded. Return it as-is.
    if (!partner.lazyConfig && !routes) {
      return partner;
    }

    const processedConfig: AnyPartnerConfiguration = partner;

    if (routes) {
      processedConfig.routes = routes.map(route => this._processPartnerRoute(route, config));
    }

    return processedConfig;
  }

  private _getPartnerLce(partnerId: string): LCEType {
    return this._partnerState[partnerId]?.type ?? LCEType.NotRequested;
  }
}
